import { cloudFunction } from '../parse';
import _ from 'lodash';
import { ILocaleMapping, ILocaleMappingForDeletion } from 'interfaces/locales';
import { baseURL, getSearchImagePlaceholder } from 'utils';

export const fetchInventoryItems = (filterData: Record<string, any>) => {
  return cloudFunction('fetchInventoryItems', filterData);
};

export const fetchInventoryItemDetails = async (id: string) => {
  const { inventoryItem } = await cloudFunction('fetchInventoryItemDetails', { id });
  return {
    ...inventoryItem,
    parentItem: inventoryItem.parentItem ? inventoryItem.parentItem.toJSON() : {},
    suppliers: inventoryItem.suppliers.map(supplier => supplier.toJSON()),
    channels: inventoryItem.channels?.map(channel => ({ value: channel, label: channel })) || [],
  };
};

export const fetchInventoryItemsByIds = (ids: any) => {
  return cloudFunction('fetchInventoryItemsByIds', { ids });
};

export const fetchCalculatedItems = async (id: string) => {
  const response = await cloudFunction('fetchCalculatedItemsForInventoryItem', { id });
  const results = JSON.parse(JSON.stringify(response));
  return results;
};

export const fetchHistoryForInventoryItem = (data: Record<string, any>) => {
  return cloudFunction('fetchHistoryForInventoryItem', data);
};

export const fetchHistoryForInventoryItemQuantityGraph = (data: Record<string, any>) => {
  return cloudFunction('fetchHistoryForInventoryItemQuantityGraph', data);
};

export const addSupplierForInventoryItem = (inventoryItemId: string, supplierId: string) => {
  return cloudFunction('addSupplierForInventoryItem', { inventoryItemId, supplierId });
};

export const removeSupplierForInventoryItem = (inventoryItemId: string, supplierId: string) => {
  return cloudFunction('removeSupplierForInventoryItem', { inventoryItemId, supplierId });
};

export const adjustInventoryItemQuantity = (id: string, quantity: number, reason: string) => {
  return cloudFunction('adjustInventoryItemQuantity', { id, quantity, reason });
};

export const adjustInventoryItemUnfufilledQuantity = (id: string, quantity: number, reason: string) => {
  return cloudFunction('adjustInventoryItemUnfufilledQuantity', { id, quantity, reason });
};

export const updateInventoryItem = (data: Record<string, any>) => {
  return cloudFunction('updateInventoryItem', data);
};

export const updateInventoryItemImage = (id: string, image: Parse.File) => {
  return cloudFunction('updateInventoryItemImage', { id, image });
};

export const updateInventoryItemNutritionalFactImage = (id: string, image: Parse.File) => {
  return cloudFunction('updateInventoryItemNutritionalFactImage', { id, nutritionalFactImage: image });
};

export const toggleActiveStatusForInventoryItem = (id: string) => {
  return cloudFunction('toggleActiveStatusForInventoryItem', { id });
};

export const toggleHomeBoxInclusion = (id: string) => {
  return cloudFunction('toggleHomeBoxInclusion', { id });
};

export const searchItems = async (data: Record<string, any>, blur?: string) => {
  data = { searchTerm: data.term || '', ...data };
  let response = await cloudFunction('searchInventoryItems', { ...data });
  response = response.values || [];
  response = response.map(res => {
    return {
      id: res.id,
      displayName: res.name,
      category: { displayName: `in ${res.categoryName || ''}` },
      image: getSearchImagePlaceholder(res.imageName, blur),
      slug: res.slug,
      enSlug: res.enSlug,
      price: res.price || 0,
    };
  });
  return response;
};

export const fetchAllCategories = async () => {
  let response = await cloudFunction('fetchAllCategories', {});
  response = response.map(row => (
    {
      objectId: row.objectId,
      description: row.description,
      slug: row.slug,
    }
  ));
  return response;
};

export const fetchSuppliersForInventoryItemForAdmin = (data: Record<string, any>) => {
  return cloudFunction('fetchSuppliersForInventoryItemForAdmin', data);
};

export const populatePurchaseOrderItemsFromImage = (data: Record<string, any>) => {
  return cloudFunction('populatePurchaseOrderItemsFromImage', data);
};

export const fetchAllUnits = () => {
  return cloudFunction('fetchAllUnits', {});
};

export const createInventoryItem = (data: Record<string, any>) => {
  return cloudFunction('createInventoryItem', data);
};

export const addRelatedItemsToItem = (itemId: string, relatedItemIds: string[]) => {
  return cloudFunction('addRelatedItemToItem', { itemId, relatedItemIds });
};

export const removeRelatedItemFromItem = (itemId: string, relatedItemIds: string[]) => {
  return cloudFunction('removeRelatedItemFromItem', { itemId, relatedItemIds });
};

export const fetchRelatedItemsForItem = (itemId: string) => {
  return cloudFunction('fetchRelatedItemsForItem', { itemId });
};

export const createComparableItem = (data: Record<string, any>) => {
  return cloudFunction('createComparableItem', data);
};

export const removeComparableItem = (id: string, comparableItemId: string) => {
  return cloudFunction('removeComparableItem  ', { id, comparableItemId });
};

export const fetchAllInventoryItemsByGroup = async () => {
  const response = await fetchInventoryItems({
    page: 0,
    limit: 5000,
    filter: {
      deleted: false,
    },
  });

  const itemsForCSV = response.items
    .filter(item => !item.parentItem)
    .map(item => (
      {
        ...item,
        enName: item.enName ?? '-',
        description: item.description ?? '-',
        url: item.objectId ? `${baseURL}/inventory-items/${item.objectId}` : '-',
        unit: item.unit ?? '-',
        cost: item.weightedAvgCost ? item.weightedAvgCost.toFixed(2) : '-',
        margin: item.weightedAvgCost ? (item.price - item.weightedAvgCost).toFixed(2) : '-',
        active: item.active ? 'Yes' : 'No',
        isPromotional: item.isPromotional ? 'Yes' : 'No',
        isFeatured: item.isFeatured ? 'Yes' : 'No',
        realStock: item.stock + (item.unfulfilledStock || 0),
        inventoryValue: item.weightedAvgCost ? (
          ((item.unfulfilledStock || 0) + item.stock) * (item.weightedAvgCost.toFixed(2))
        ) : '-',
        difference: 0,
        loss: '',
      }
    ));

  return {
    itemsForCSV,
    categories: _.groupBy(
      response.items.filter(item => !item.parentItem),
      (val) => val.category.description),
  };
};

export const bulkUpdateInventoryItems = (data: Record<string, any>) => {
  return cloudFunction('bulkUpdateInventoryItems', data);
};

export const deleteInventoryItem = (inventoryItemId: string) => {
  return cloudFunction('deleteInventoryItem', { inventoryItemId });
};

export const addLocale = (data: ILocaleMapping) => {
  return cloudFunction('addLocale', data);
};

export const editLocale = (data: ILocaleMapping) => {
  return cloudFunction('editLocale', data);
};

export const deleteLocale = (data: ILocaleMappingForDeletion) => {
  return cloudFunction('deleteLocale', data);
};

export const createCalculatedInventoryItem = (data: Record<string, any>) => {
  return cloudFunction('createCalculatedInventoryItem', data);
};

export const getKeywords = (id: string) => {
  return cloudFunction('getKeywords', { id });
};

export const addKeywords = (data: Record<string, any>) => {
  return cloudFunction('addKeywords', data);
};

export const removeKeywords = (data: Record<string, any>) => {
  return cloudFunction('removeKeywords', data);
};

export const addChannelToInventoryItem = (data: Record<string, any>) => {
  return cloudFunction('addChannelToInventoryItem', data);
};

export const removeChannelFromInventoryItem = (data: Record<string, any>) => {
  return cloudFunction('removeChannelFromInventoryItem', data);
};


export const addInventoryItemTag = (id: string, tagIds: string[]) => {
  return cloudFunction('assignTagsToInventoryItem', { id, tagIds });
};

export const fetchInventoryItemTags = (id) => {
  return cloudFunction('fetchTagsForInventoryItem', { id });
};

export const removeInventoryItemTag = (id: string, tagId: string) => {
  return cloudFunction('removeTagFromInventoryItem', { id, tagId });
};

export const fetchInactiveInventoryItemsWithStock = () => {
  return cloudFunction('inactiveInventoryItemWithStockReport', {});
};

export const addInventoryItemBlog = (data: Record<string, any>) => {
  return cloudFunction('addInventoryItemBlog', data);
};

export const updateInventoryItemBlog = (data: Record<string, any>) => {
  return cloudFunction('updateInventoryItemBlog', data);
};

export const removeInventoryItemBlog = (data: Record<string, any>) => {
  return cloudFunction('removeInventoryItemBlog', data);
};

export const removeInventoryItemAddress = (data: Record<string, any>) => {
  return cloudFunction('removeInventoryItemAddress', data);
};

export const setInventoryItemAddress = (data: Record<string, any>) => {
  return cloudFunction('setInventoryItemAddress', data);
};

export const fetchHistoryForInventoryItemStatus = (params: any) => {
  return cloudFunction('fetchHistoryForInventoryItemStatus', params);
};

export const resetUnfulfilledQuantityForAllItems = () => {
  return cloudFunction('resetUnfulfilledQuantityForAllItems', {});
};

export const fetchInventoryItemsForInventorySpotChecks = (params?: { limit: number }) => {
  return cloudFunction('fetchInventoryItemsForInventorySpotChecks', params || {});
};

export const markInventorySpotCheck = (params: any) => {
  return cloudFunction('markInventorySpotCheck', params);
};